import {
	FormDataTypes,
	MapToConversionType,
	MaritalStatusIds,
	PropertyUseIds,
	FormFileMetadata,
} from '@models/client-intake';
import type { FormValue } from '@models/client-intake';
import type { EngineFormItemFormField } from '@models/client-portal/engine-client-intake';
import { isSet } from '@globalUtils/utilities/value';
import { MapTo } from '@models/client-intake';
import {
	Matter,
	PersonMatterClient,
	SpousalStatus,
	SpousalStatusDisplay,
} from '@models/matter';
import {
	PersonClient,
	RepresentationType,
	BusinessClient,
} from '@models/clients';
import { PropertyUsage, PropertyUsageDisplay } from '@models/title-insurance';

/**
 * Context containing all possible data sources for populating default values.
 * Pass the appropriate data based on form type (client vs general/matter).
 */
export interface DefaultValueContext {
	matter?: Matter;
	client?: PersonClient;
	matterClient?: PersonMatterClient;
	businessClient?: BusinessClient;
}

export interface DisplayOption {
	id: string | number;
	label?: string;
}

/**
 * Navigate to a value in an object using a dot-separated path
 */
function getValueFromPath(obj: unknown, path: string): unknown {
	if (!obj || !path) return undefined;

	const keys = path.split('.');
	let current: unknown = obj;

	for (const key of keys) {
		if (
			current === null ||
			current === undefined ||
			typeof current !== 'object'
		) {
			return undefined;
		}
		current = (current as Record<string, unknown>)[key];
	}

	return current;
}

/**
 * Unified function to extract a value from the data context based on mapTo configuration.
 * Handles all collection types and conversion types consistently.
 *
 * @param mapTo - The mapTo configuration from the field
 * @param context - The data context containing matter, client, matterClient, businessClient
 * @param options - Optional field options for boolean-to-option conversion
 * @returns The extracted and converted value, or undefined if not found
 */
export function extractValueFromMapTo(
	mapTo: MapTo | undefined | null,
	context: DefaultValueContext,
	options?: EngineFormItemFormField['options'],
): FormValue {
	if (!mapTo || !mapTo.field) return undefined;

	const { collection, field, conversionType } = mapTo;

	let data: unknown;
	switch (collection) {
		case FormDataTypes.Matter:
			data = context.matter;
			break;
		case FormDataTypes.Client:
			data = context.client;
			break;
		case FormDataTypes.MatterClient:
			data = context.matterClient;
			break;
		case FormDataTypes.BusinessClient:
			data = context.businessClient;
			break;
		default:
			return undefined;
	}

	if (!data) return undefined;

	// curretnly passing in both data and context to applyConversionType to handle the case where the collection was input wrong
	if (conversionType && conversionType !== MapToConversionType.NONE) {
		return applyConversionType(conversionType, data, field, context);
	}

	const rawValue = getValueFromPath(data, field);
	if (rawValue === undefined || rawValue === null) return undefined;

	// keeping this for now to ensure this still works for all boolean values incase an item is missing the conversion type
	if (typeof rawValue === 'boolean' && options) {
		const matchingOption = options.find(
			(opt) =>
				opt.id === String(rawValue) ||
				(rawValue === true && (opt.id === 'yes' || opt.id === 'Yes')) ||
				(rawValue === false && (opt.id === 'no' || opt.id === 'No')),
		);
		if (matchingOption) {
			return matchingOption.id as FormValue;
		}
	}

	return rawValue as FormValue;
}

/**
 * Apply special conversion types that transform values in specific ways
 */
function applyConversionType(
	conversionType: MapToConversionType,
	data: unknown,
	fieldPath: string,
	context: DefaultValueContext,
): FormValue {
	switch (conversionType) {
		case MapToConversionType.YES_NO_TO_BOOLEAN: {
			const value = getValueFromPath(data, fieldPath);
			if (value === true) return 'yes';
			if (value === false) return 'no';
			return undefined;
		}

		case MapToConversionType.SPOUSAL_STATUS: {
			const matterClient = context.matterClient ?? (data as PersonMatterClient);
			const spousalStatus = matterClient?.fla?.spousalStatus;

			switch (spousalStatus) {
				case SpousalStatus.SPOUSE_TO:
					return MaritalStatusIds.MARRIED;
				case SpousalStatus.NOT_A_SPOUSE:
					return MaritalStatusIds.SINGLE;
				default:
					return undefined;
			}
		}

		case MapToConversionType.REP_BY_POA: {
			const client = context.client ?? (data as PersonClient);
			const representation = client?.representation;

			if (representation === RepresentationType.ATTORNEY) return 'yes';
			if (representation === RepresentationType.NONE) return 'no';
			return undefined;
		}

		case MapToConversionType.ESTATE: {
			const client = context.client ?? (data as PersonClient);
			const representation = client?.representation;

			if (representation === RepresentationType.ESTATE) return 'yes';
			if (representation === RepresentationType.NONE) return 'no';
			return undefined;
		}

		case MapToConversionType.INTENDED_USE_OF_PROPERTY: {
			const matter = context.matter ?? (data as Matter);
			const usage = matter?.propertyDetails?.propertyType?.usage;

			if (usage === PropertyUsage.RESIDENTIAL) {
				return PropertyUseIds.PRIMARY_RESIDENCE;
			}
			return undefined;
		}

		case MapToConversionType.CLIENT_IDENTIFICATION:
		case MapToConversionType.SPOUSE_CLIENT_ID:
			// These are handled differently - just extract the raw value
			return getValueFromPath(data, fieldPath) as FormValue;

		default:
			return getValueFromPath(data, fieldPath) as FormValue;
	}
}

export function getDisplayValue(
	answer: FormValue | FormFileMetadata,
	options?: DisplayOption[],
	extraMatterDetails?: string[],
	conversionType?: MapToConversionType,
): string {
	if (!isSet(answer)) {
		return '-';
	}

	switch (conversionType) {
		case MapToConversionType.SPOUSAL_STATUS: {
			switch (answer) {
				case MaritalStatusIds.MARRIED:
					return SpousalStatusDisplay.SPOUSE_TO;
				case MaritalStatusIds.SINGLE:
					return SpousalStatusDisplay.NOT_A_SPOUSE;
				default:
					return String(answer);
			}
		}
		case MapToConversionType.INTENDED_USE_OF_PROPERTY: {
			switch (answer) {
				case PropertyUseIds.PRIMARY_RESIDENCE:
				case PropertyUseIds.SECONDARY_RESIDENCE:
					return PropertyUsageDisplay.RESIDENTIAL;
				default:
					return String(answer);
			}
		}
		default:
			break;
	}

	if (extraMatterDetails && extraMatterDetails.length > 0) {
		return extraMatterDetails.join(', ');
	}

	if (options && typeof answer === 'string') {
		const matchingOption = options.find((opt) => opt.id === answer);
		if (matchingOption?.label) {
			return matchingOption.label;
		}
	}

	if (
		typeof answer === 'object' &&
		!Array.isArray(answer) &&
		answer !== null &&
		'fileName' in answer
	) {
		return (answer as FormFileMetadata).fileName;
	}

	if (Array.isArray(answer)) {
		if (options) {
			return answer
				.map((val) => {
					const opt = options.find((o) => o.id === val);
					return opt?.label ?? String(val);
				})
				.join(', ');
		}
		return answer.join(', ');
	}

	const stringValue = String(answer);
	//TODO: Temporary to handle long file paths in Closer, really each file should have a friendly name
	if (stringValue.split('/').length > 3) {
		return stringValue.split('/')[stringValue.split('/').length - 1];
	}

	return stringValue;
}
